// Config de descarga por marca (`brand_profiles.descarga_config`, jsonb) —
// ADR-2026-09-14-configuracion-de-descarga.
//
// Gemela de `watermarkConfig`: misma tabla, mismo cache, misma lectura defensiva
// (una config vieja o corrupta nunca rompe una descarga; en el peor caso se baja el
// original, que es lo que pasaba antes de que esto existiera).
//
// Lo que guarda son los formatos de salida —los que piden las tiendas de la empresa—
// y una sola decisión de cuándo aplicarlos:
//
//   · `siempre: null` (el default): cada descarga elige entre el original y los
//     formatos guardados. Es el caso real —"algunas imágenes nada más, no todas"— y por
//     eso es el default: una config que se aplica sola convierte también las que
//     nadie pidió convertir, y el recorte no se deshace.
//   · `siempre: <id>`: ESE formato sale sin preguntar. Lo prende quien SIEMPRE entrega
//     a la misma tienda.
//
// Los formatos son una LISTA (ADR-2026-09-14-varios-formatos-de-descarga): una empresa
// vende en más de una tienda y cada una pide su medida.

import { supabase } from '../lib/supabase';
import type { FormatoDeSalida } from './formatoDeSalida';

export type { FormatoDeSalida };

/** Un formato de salida con nombre: lo que una tienda pide. */
export interface PerfilDeDescarga {
  /** Generado; es lo que la hoja de descarga elige y lo que `siempre` señala. */
  id: string;
  /** Cómo lo ve el usuario en la hoja ("Tiendanube"). Vacío = se muestra la medida. */
  nombre: string;
  ancho: number;
  alto: number;
  formato: FormatoDeSalida;
  /** Techo de peso. `null` = sin techo: se encoda una sola vez, en la mejor calidad. */
  pesoMaxKB: number | null;
}

export interface ConfigDeDescarga {
  version: 2;
  /** Hay formatos guardados y se ofrecen al descargar. */
  activo: boolean;
  /**
   * El id del perfil que se aplica SIN preguntar; `null` = cada descarga elige.
   * A lo sumo uno: con varios formatos, "siempre" es de uno en particular
   * (ADR-2026-09-14-varios-formatos-de-descarga).
   */
  siempre: string | null;
  perfiles: PerfilDeDescarga[];
}

/** Abajo de esto no es una foto; arriba, ningún destino la pide. */
export const ANCHO_MIN = 64;
export const ANCHO_MAX = 8000;
/** Un techo de 20 KB ya es ilegible; 10 MB es no tener techo. */
export const PESO_MIN_KB = 20;
export const PESO_MAX_KB = 10_000;

/** La opción de la hoja que NO convierte. Ningún perfil puede llamarse así. */
export const ORIGINAL = 'original';

/**
 * Un perfil recién agregado. Es 1000×1500 WebP porque es la medida que pide una tienda
 * típica, no porque la app la prefiera: nada se aplica hasta que alguien lo prende.
 */
export function nuevoPerfil(): PerfilDeDescarga {
  return {
    id: `p_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
    nombre: '',
    ancho: 1000,
    alto: 1500,
    formato: 'webp',
    pesoMaxKB: 200,
  };
}

/** Con qué se abre la card la primera vez: un formato para editar, apagado hasta guardar. */
export function defaultConfigDeDescarga(): ConfigDeDescarga {
  return { version: 2, activo: true, siempre: null, perfiles: [nuevoPerfil()] };
}

const clamp = (v: unknown, lo: number, hi: number, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0
    ? Math.round(Math.min(hi, Math.max(lo, v)))
    : dflt;

function normalizePerfil(raw: unknown): PerfilDeDescarga | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const d = nuevoPerfil();
  const peso =
    typeof r.pesoMaxKB === 'number' && Number.isFinite(r.pesoMaxKB) && r.pesoMaxKB > 0
      ? clamp(r.pesoMaxKB, PESO_MIN_KB, PESO_MAX_KB, d.pesoMaxKB!)
      : null;
  // Un id ausente (o reservado) deja al perfil sin forma de ser elegido: se le inventa.
  const id = typeof r.id === 'string' && r.id.trim() && r.id !== ORIGINAL ? r.id : d.id;
  return {
    id,
    nombre: typeof r.nombre === 'string' ? r.nombre.slice(0, 60) : '',
    ancho: clamp(r.ancho, ANCHO_MIN, ANCHO_MAX, d.ancho),
    alto: clamp(r.alto, ANCHO_MIN, ANCHO_MAX, d.alto),
    formato:
      r.formato === 'jpg' || r.formato === 'png' || r.formato === 'webp' ? r.formato : d.formato,
    pesoMaxKB: peso,
  };
}

/**
 * Merge defensivo de un jsonb crudo sobre los defaults. `null` = no hay nada usable.
 *
 * Lee también la PRIMERA versión (un solo formato con los campos planos y `siempre`
 * booleano): queda como un perfil único, y su "siempre" pasa a señalarlo. Nunca se
 * reescribe en la base por leerla; se reescribe recién cuando el usuario guarda.
 */
export function normalizeConfigDeDescarga(raw: unknown): ConfigDeDescarga | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // Sin ninguna llave reconocible es un jsonb de otra cosa (o un `{}` recién creado).
  if (!('activo' in r || 'perfiles' in r || 'ancho' in r || 'alto' in r || 'formato' in r)) {
    return null;
  }
  const activo = typeof r.activo === 'boolean' ? r.activo : true;

  if (Array.isArray(r.perfiles)) {
    const perfiles = r.perfiles
      .map(normalizePerfil)
      .filter((p): p is PerfilDeDescarga => p !== null);
    // Un "siempre" que apunta a un perfil que ya no está no puede aplicar nada.
    const siempre =
      typeof r.siempre === 'string' && perfiles.some((p) => p.id === r.siempre) ? r.siempre : null;
    return { version: 2, activo, siempre, perfiles };
  }

  // Primera versión: los campos del único formato vivían en la raíz.
  const unico = normalizePerfil(r)!;
  return { version: 2, activo, siempre: r.siempre === true ? unico.id : null, perfiles: [unico] };
}

/** Qué hace la descarga con esta config. */
export type ModoDeDescarga = 'original' | 'perfil' | 'preguntar';

export function modoDeDescarga(config: ConfigDeDescarga | null): ModoDeDescarga {
  if (!config?.activo || config.perfiles.length === 0) return 'original';
  return config.siempre !== null ? 'perfil' : 'preguntar';
}

/**
 * El perfil que corresponde a una elección de la hoja, o `null` si es el original o si
 * el perfil ya no está (lo pudieron borrar entre la hoja y la descarga): en los dos
 * casos no se convierte nada.
 */
export function perfilElegido(
  config: ConfigDeDescarga | null,
  eleccion: string,
): PerfilDeDescarga | null {
  if (!config || eleccion === ORIGINAL) return null;
  return config.perfiles.find((p) => p.id === eleccion) ?? null;
}

// ── Persistencia ────────────────────────────────────────────────────────────
// Cache en memoria por marca, como la de la marca de agua: la lee CADA descarga y una
// tanda de 24 imágenes pegaría 24 veces a la base.

const cache = new Map<string, ConfigDeDescarga | null>();

export function invalidarCacheDeDescarga(): void {
  cache.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('atlas:brand-changed', invalidarCacheDeDescarga);
}

/**
 * Cuántas veces se guardó cada marca. Una lectura que arrancó ANTES de una escritura trae la
 * lista vieja: si al volver ve que el número cambió, no la guarda en la caché (pisaría lo
 * recién guardado). Revisión de Greptile al PR #197, cuarta vuelta.
 */
const escrituras = new Map<string, number>();

export async function fetchConfigDeDescarga(brandId: string): Promise<ConfigDeDescarga | null> {
  if (cache.has(brandId)) return cache.get(brandId) ?? null;
  const antes = escrituras.get(brandId) ?? 0;
  try {
    const { data, error } = await supabase
      .from('brand_profiles')
      .select('descarga_config')
      .eq('id', brandId)
      .maybeSingle();
    const config = error ? null : normalizeConfigDeDescarga(data?.descarga_config);
    // Se escribió mientras leíamos: lo de la caché (lo guardado) manda.
    if ((escrituras.get(brandId) ?? 0) !== antes) return cache.get(brandId) ?? config;
    cache.set(brandId, config);
    return config;
  } catch {
    return null; // sin config ⇒ la descarga sigue como siempre
  }
}

/**
 * La config de la base, sin cache y distinguiendo "no hay" de "no se pudo leer". Para quien
 * va a ESCRIBIR sobre lo leído: `fetchConfigDeDescarga` devuelve `null` en los dos casos, y
 * tomar un error por "no hay" pisaba la lista entera con un solo formato
 * (ADR-2026-10-21-revision-greptile-196-formatos-personalizados).
 */
export async function leerConfigDeDescargaParaEscribir(
  brandId: string,
): Promise<{ ok: true; config: ConfigDeDescarga | null } | { ok: false }> {
  try {
    const { data, error } = await supabase
      .from('brand_profiles')
      .select('descarga_config')
      .eq('id', brandId)
      .maybeSingle();
    // Sin fila (otra marca, o la RLS que no la deja ver) tampoco es "no hay config".
    if (error || !data) return { ok: false };
    return { ok: true, config: normalizeConfigDeDescarga(data.descarga_config) };
  } catch {
    return { ok: false };
  }
}

export async function guardarConfigDeDescarga(
  brandId: string,
  config: ConfigDeDescarga,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('brand_profiles')
    .update({ descarga_config: config })
    .eq('id', brandId);
  if (error) return { ok: false, error: error.message };
  escrituras.set(brandId, (escrituras.get(brandId) ?? 0) + 1);
  cache.set(brandId, config);
  // Los selectores de formato muestran estos mismos formatos
  // (ADR-2026-10-20-formatos-personalizados): se enteran sin volver a consultar.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('atlas:descarga-config-guardada', { detail: { brandId, config } }));
  }
  return { ok: true };
}

// ── Última elección del chooser (por dispositivo) ───────────────────────────

/** `'original'` o el id de un perfil. El caller valida contra la lista viva. */
export type EleccionDeFormato = string;

const CLAVE_ELECCION = 'atlas:formato-descarga-eleccion';

/**
 * Con qué opción se abre la hoja de descarga. El default es el original a propósito:
 * el formato de salida recorta, y un recorte que nadie eligió no se deshace. Quien
 * quiere que salga convertido siempre tiene el interruptor `siempre` en Configuración.
 */
export function getUltimaEleccionDeFormato(): EleccionDeFormato {
  try {
    return localStorage.getItem(CLAVE_ELECCION) || ORIGINAL;
  } catch {
    return ORIGINAL;
  }
}

export function setUltimaEleccionDeFormato(e: EleccionDeFormato): void {
  try {
    localStorage.setItem(CLAVE_ELECCION, e);
  } catch {
    /* modo privado / storage lleno: la preferencia no se recuerda, nada más */
  }
}
